import { useCallback, useRef } from 'react'

import { PointArray } from '@editor/interfaces/attribute'
import { MeasurementLabelProps } from '@editor/interfaces/canvas/measurementLabel'
import useProjectionPlane from '@editor/pages/projects/blueprint-editor/hooks/useProjectionPlane'
import { convertPrintDimension } from '@editor/services/Util'
import { useThree } from '@react-three/fiber'
import { Vector3 } from 'three'

import { expandRectangle3D } from '../utils'

const vector0 = new Vector3(0, 0, 0)

// Constants for measurement label dimensions
const DASH_SIZE_POINTS = 2
const GAP_SIZE_POINTS = 1.5
const ARROW_LENGTH_POINTS = 7
const ARROW_WIDTH_POINTS = 2.5
const DIMENSION_SCALE_MULTIPLIER = 1000

const useUtilities = ({ printScale: rawPrintScale }: MeasurementLabelProps) => {
  // Hooks
  const { camera, gl } = useThree()
  const { projectionDirections } = useProjectionPlane(camera, gl)

  // Declare vectors to avoid re-creating them on every render
  const tempVector1 = useRef(new Vector3())
  const tempVector2 = useRef(new Vector3())
  const tempVectorOffset1 = useRef(new Vector3())
  const tempVectorOffset2 = useRef(new Vector3())
  const tempLeftVector = useRef(new Vector3())

  // Derived
  const printScale = Number(rawPrintScale ?? 0.01)

  /**
   * Calculate dash and gap sizes for offset lines based on print scale
   * @returns Object containing dashSize and gapSize in Three.js world units, scaled for material usage
   */
  const calculateDashGapSizes = useCallback(() => {
    const dashSize = convertPrintDimension(DASH_SIZE_POINTS, printScale) * DIMENSION_SCALE_MULTIPLIER
    const gapSize = convertPrintDimension(GAP_SIZE_POINTS, printScale) * DIMENSION_SCALE_MULTIPLIER
    return { dashSize, gapSize }
  }, [printScale])

  /**
   * Calculate arrow dimensions based on print scale for consistent visual appearance
   * @returns Object containing arrowLength and arrowWidth in Three.js world units (meters)
   */
  const calculateArrowSizes = useCallback(() => {
    const arrowLength = convertPrintDimension(ARROW_LENGTH_POINTS, printScale)
    const arrowWidth = convertPrintDimension(ARROW_WIDTH_POINTS, printScale)
    return { arrowLength, arrowWidth }
  }, [printScale])

  /**
   * Calculate the points for the hover container based on the provided positions and offset.
   */
  const calculateContainerPoints = useCallback(
    (pos: [Vector3, Vector3], offsetPos: [Vector3, Vector3], inOffset: number) => {
      if (!projectionDirections) return []

      const rectPoints = [pos[0], pos[1], offsetPos[1], offsetPos[0]]
      return expandRectangle3D(rectPoints, projectionDirections.normal, [
        inOffset >= 0 ? 0.04 : 0.0175,
        0.0175,
        inOffset >= 0 ? 0.0175 : 0.04,
        0.0175,
      ])
    },
    [projectionDirections],
  )

  /**
   * Calculate the offset positions based on the current points and projection directions.
   */
  const calculateOffsetPositions = useCallback(
    (point1: PointArray, point2: PointArray, ofs: number): [Vector3, Vector3] => {
      if (!projectionDirections) {
        if (point1 && point2) {
          return [tempVector1.current.fromArray(point1).clone(), tempVector2.current.fromArray(point2).clone()]
        }

        return [vector0, vector0]
      }

      const direction = new Vector3()
        .subVectors(
          tempVector1.current.set(point1[0], point1[1], point1[2]),
          tempVector2.current.set(point2[0], point2[1], point2[2]),
        )
        .normalize()
      tempLeftVector.current.copy(direction).cross(projectionDirections.normal).normalize()

      const left = tempLeftVector.current.clone().multiplyScalar(ofs / 1000)

      return [
        tempVectorOffset1.current.copy(tempVector1.current).add(left).clone(),
        tempVectorOffset2.current.copy(tempVector2.current).add(left).clone(),
      ]
    },
    [projectionDirections],
  )

  return {
    calculateDashGapSizes,
    calculateArrowSizes,
    calculateContainerPoints,
    calculateOffsetPositions,
  }
}

export default useUtilities
